import type { ReactNode } from 'react'

import { usePermissions } from '../admin/primitives.js'
import { onForbidden } from '../shell/auth-routing.js'

/**
 * GARDE DE ROUTE — LA SEULE CHOSE QUI MÈNE À « ACCÈS REFUSÉ ».
 *
 * Avant, n'importe quel appel API refusé détournait le document entier vers
 * /access-denied. Un Super Admin parfaitement autorisé y atterrissait donc dès
 * qu'un widget secondaire échouait — d'où un « Accès refusé » qui semblait
 * aléatoire, puisqu'il dépendait de l'appel qui perdait la course.
 *
 * Deux règles, et rien d'autre :
 *
 *   · TANT QUE LES DROITS NE SONT PAS CONNUS, on n'affirme rien. Un squelette,
 *     pas un refus : « pas encore su » n'est pas « interdit » ;
 *   · une fois connus, l'absence du droit demandé refuse la ROUTE — et
 *     seulement elle. Plusieurs droits peuvent être acceptés (UN SEUL
 *     suffit) : une page lisible par l'équipe OU par soi-même ne doit pas
 *     être refusée à celui qui n'a que le second droit — le serveur tranche
 *     ensuite le périmètre réel.
 *
 * Le serveur est inchangé : il refuse ce qu'il refusait déjà. Cette garde ne
 * décide de rien, elle évite d'annoncer un refus que personne n'a prononcé.
 */
export function RequirePermission({ perm, children }: {
  perm: string | string[]
  children: ReactNode
}) {
  const { permissions, loading } = usePermissions()

  if (loading) return <PermissionSkeleton />

  const accepted = Array.isArray(perm) ? perm : [perm]
  if (!accepted.some((p) => permissions.includes(p))) {
    // Même destination qu'avant pour un refus RÉEL : rien n'est assoupli.
    onForbidden()
    return <PermissionSkeleton />
  }

  return <>{children}</>
}

/**
 * Attente sobre : la page arrive, elle n'est pas refusée.
 *
 * Réutilise `so-skel`, le squelette déjà employé ailleurs dans Soirées — pas
 * de nouvelle classe inventée pour l'occasion.
 */
function PermissionSkeleton() {
  return (
    <main className="lc-page lc-stagger" aria-busy="true">
      <div className="so-skel" style={{ maxWidth: 420 }}><span /><span /><span /></div>
    </main>
  )
}
